import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  cn,
  resolveBackgroundImageStyle,
  type BackgroundImageProps,
  warnOnce,
} from '@/hen-ui/utils';
import {
  CardLinkProvider,
  useCardLinkContext,
} from '@/hen-ui/hooks/cardLinkContext';
import { isSafeInlineLinkHref } from '@/hen-ui/lib/inline-copy';
import {
  type CardHoverShadow,
  type CardVariantProps,
  cardVariants,
  cardLinkShellClasses,
  type CardContentVariantProps,
  cardContentVariants,
} from './card.variants';

export type CardLinkTarget = '_self' | '_blank';

/**
 * A flexible card component for displaying grouped content.
 *
 * Supports compound component pattern with Card.Content and Card.Footer subcomponents.
 * Optional CSS background-image props (`backgroundImage`, `backgroundSize`,
 * `backgroundRepeat`, `backgroundPosition`) paint on the root — prefer
 * `variant="background-transparent"` when the photo should dominate opaque
 * background variants.
 *
 * @example
 * ```tsx
 * <Card variant="background-accent">
 *   <Card.Content>
 *     <h3>Card Title</h3>
 *     <p>Card content</p>
 *   </Card.Content>
 *   <Card.Footer>
 *     <span>Footer text</span>
 *   </Card.Footer>
 * </Card>
 * ```
 *
 * @example Background image
 * ```tsx
 * <Card
 *   variant="background-transparent"
 *   backgroundImage="https://cdn.example.com/partner-hero.jpg"
 *   rounded="lg"
 * >
 *   <Card.Content>
 *     <h3>Partner highlight</h3>
 *   </Card.Content>
 * </Card>
 * ```
 *
 * @example Click action
 * ```tsx
 * // A full-card button sits beneath the content: the whole card is one tab
 * // stop named by `ariaLabel`, and nested links and buttons keep their own
 * // clicks and tab stops.
 * <Card ariaLabel="Open program details" onClick={openDetails}>
 *   <Card.Content>
 *     <h3>Program name</h3>
 *     <Link href="/apply">Apply</Link>
 *   </Card.Content>
 * </Card>
 * ```
 *
 * @example Link card
 * ```tsx
 * // The same overlay renders as a link. A nested Button or Link to the same
 * // destination is visual only, so the card stays one tab stop.
 * <Card href="/financing" variant="background-primary-tint">
 *   <Card.Content>
 *     <h3>Financing options</h3>
 *     <Button href="/financing">See options</Button>
 *   </Card.Content>
 * </Card>
 * ```
 */
export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    CardVariantProps,
    BackgroundImageProps {
  /**
   * When true, the root renders as Radix `Slot` and merges props onto the single child (e.g. `a` or `button`).
   * The default passive `hen.card` id is not applied. A consumer `data-hen-id` on `Card` is forwarded onto the
   * child; the child’s own `data-hen-id` can still win after merge—same precedence as `ModalTrigger` / `DrawerTrigger`.
   */
  asChild?: boolean;
  /**
   * When false, no passive `data-hen-id` is emitted on the root (e.g. nested decorative shells).
   * Only applies when not using `asChild`. Default true.
   */
  trackable?: boolean;
  /**
   * Override passive catalog id on the root. Defaults to `hen.card` when `trackable` and not `asChild`.
   * With `asChild`, the default `hen.card` is omitted; a consumer `data-hen-id` is still forwarded onto the
   * child via Slot (the child’s own `data-hen-id` can still override after merge).
   */
  'data-hen-id'?: string;
  /**
   * When set, the full-card overlay is a link to this destination instead of
   * a button. Unsafe schemes (e.g. `javascript:`) are ignored and the card
   * renders as a plain container. Ignored with `asChild`.
   *
   * The link wins: `onClick` still runs on every card click, for side effects
   * such as tracking, and the browser then follows `href` unless `onClick`
   * calls `preventDefault()`. Cmd/Ctrl/Shift-click and middle-click on the card
   * open `href` in a new tab. A drag that selects card text does not follow it.
   *
   * A nested Button or Link to the same destination, or a Button with no
   * action, renders as visual only. This also applies to content portalled
   * from inside the card, such as a dialog opened by one of its controls.
   */
  href?: string;
  /** Link target; `_blank` adds `rel="noopener noreferrer"`. */
  target?: CardLinkTarget;
  /**
   * Label read by screen readers when the card is a link or has a click
   * action. Otherwise it is not rendered. Ignored with `asChild`, where the
   * child owns the element. Left empty, the overlay is named from all of the
   * card's text instead.
   */
  ariaLabel?: string;
  /** Resting elevation from the theme's `--shadow-*` scale. When set it wins over a
   * legacy `border="shadow"`; `none` removes that shadow. */
  shadow?: CardVariantProps['shadow'];
  /** Elevation on hover and card-link focus; `interactive` reads `--shadow-hover`
   * (falls back to `--shadow-md`). Link cards only (a safe `href`, no `asChild`). */
  hoverShadow?: CardHoverShadow;
}

/**
 * Clicks that start on one of these inside a bound card belong to that
 * control, not to the card action.
 */
const CARD_NESTED_INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  'label',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[contenteditable="true"]',
].join(', ');

const CARD_ACTION_CLASS_NAME = cn(
  'absolute inset-0 -z-10 m-0 rounded-[inherit] border-0 bg-transparent p-0',
  'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-focus',
);

// Clipping clips the action's ring away, so focus moves to the card's outline.
const CARD_ACTION_CLIPPED_CLASS_NAME = cn(
  CARD_ACTION_CLASS_NAME,
  'focus-visible:ring-0 focus-visible:ring-offset-0',
);

// Focus ring on the card's outline (unclipped), per-utility `:has()` prefix.
const CARD_CLIPPED_FOCUS_CLASS_NAME =
  '[&:has(>[data-slot=card-action]:focus-visible)]:outline [&:has(>[data-slot=card-action]:focus-visible)]:outline-2 [&:has(>[data-slot=card-action]:focus-visible)]:outline-offset-1 [&:has(>[data-slot=card-action]:focus-visible)]:outline-focus';

type CardComponent = typeof Card & {
  Content: typeof CardContent;
  Footer: typeof CardFooter;
};

export interface CardContentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    CardContentVariantProps {
  asChild?: boolean;
  /** Legacy class override for one-off content padding. Prefer `contentPadding` for new code. */
  padding?: string;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  /** Background color class for the footer. Default: 'bg-secondary-50'. */
  background?: string;
  /** Padding for the footer. Default: 'py-d3 px-lg md:px-xl'. */
  padding?: string;
  /** Bottom corner radius; should match parent Card's rounded variant. Default: 'lg'. */
  rounded?: 'md' | 'lg';
}

interface CardBaseProps extends CardProps {
  /** Whether `onClick` or `href` renders the full-card overlay. Never author-set. */
  actionOverlay: boolean;
}

/**
 * Supplies an id for a bound card's button to be named from. Only bound
 * cards mount it, so unbound cards keep the ids React generates for them.
 */
function WithGeneratedId({
  children,
}: {
  children: (id: string) => React.ReactElement;
}) {
  return children(React.useId());
}

/** Cmd, Ctrl, or Shift held on a link click asks for a new tab or window. */
function requestsNewTab(event: React.MouseEvent<HTMLElement>): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey;
}

/** Whether the user has just drag-selected text inside the card. */
function hasTextSelectionIn(root: HTMLElement): boolean {
  const selection = root.ownerDocument.defaultView?.getSelection();
  if (!selection || selection.isCollapsed) return false;
  return (
    (selection.anchorNode != null && root.contains(selection.anchorNode)) ||
    (selection.focusNode != null && root.contains(selection.focusNode))
  );
}

/** Opens the card link in a new tab, with the same `rel` as the overlay link. */
function openLinkInNewTab(
  link: HTMLAnchorElement,
  target: CardLinkTarget | undefined,
): void {
  link.ownerDocument.defaultView?.open(
    link.href,
    '_blank',
    target === '_blank' ? 'noopener,noreferrer' : 'noopener',
  );
}

/**
 * Follows the card link for a click on the card body: in a new tab for
 * Cmd/Ctrl/Shift-click, middle-click, or `target="_blank"`, otherwise in this
 * tab. The click stays the user's own event, so trackers listening above the
 * card see it once, as a trusted click.
 */
function followCardLink(
  link: HTMLAnchorElement,
  target: CardLinkTarget | undefined,
  newTab: boolean,
): void {
  if (newTab || target === '_blank') {
    openLinkInNewTab(link, target);
    return;
  }
  link.ownerDocument.defaultView?.location.assign(link.href);
}

const CardBase = React.forwardRef<HTMLDivElement, CardBaseProps>(
  (
    {
      actionOverlay,
      className,
      variant,
      border,
      padding,
      shadow,
      hoverShadow,
      rounded,
      clipContent,
      sectionGap,
      height,
      asChild = false,
      trackable = true,
      children,
      backgroundImage,
      backgroundSize,
      backgroundRepeat,
      backgroundPosition,
      style,
      'data-hen-id': dataHenIdFromConsumer,
      href,
      target: linkTarget,
      ariaLabel,
      onClick,
      onAuxClick,
      ...props
    },
    ref,
  ) => {
    const actionRef = React.useRef<HTMLElement>(null);
    const parentCardLink = useCardLinkContext();
    const Component = asChild ? Slot : 'div';
    const dataHenId = asChild
      ? dataHenIdFromConsumer
      : trackable
        ? (dataHenIdFromConsumer ?? COMPONENT_IDS.Card)
        : undefined;
    const trimmedHref = href?.trim();
    const linkHref =
      actionOverlay &&
      !asChild &&
      trimmedHref &&
      isSafeInlineLinkHref(trimmedHref)
        ? trimmedHref
        : undefined;
    const isBound =
      actionOverlay && !asChild && (linkHref != null || onClick != null);
    const hasLabel = Boolean(ariaLabel?.trim());
    const labelFromContent = isBound && !hasLabel;
    if (process.env.NODE_ENV !== 'production' && labelFromContent) {
      warnOnce(
        'Card/click-action-without-label',
        "[hen-ui/Card] A Card with a click action or link has no `ariaLabel`, so screen readers name it from all of the card's text. Set `ariaLabel` to a short name.",
      );
    }
    const backgroundStyle = resolveBackgroundImageStyle({
      backgroundImage,
      backgroundSize,
      backgroundRepeat,
      backgroundPosition,
    });
    const mergedStyle =
      backgroundStyle || style ? { ...backgroundStyle, ...style } : undefined;

    /** A click on card content, not on the overlay, a nested control, or a portal. */
    const isBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as Element;
      // React bubbles portal events through the tree; those aren't card clicks.
      if (!event.currentTarget.contains(target)) return false;
      if (target === actionRef.current) return false;
      const control = target.closest(CARD_NESTED_INTERACTIVE_SELECTOR);
      return !(control && event.currentTarget.contains(control));
    };

    const handleBoundClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const action = actionRef.current;
      if (event.target !== action) {
        if (!isBodyClick(event)) return;
        // Releasing a drag that selected card text is not a card click.
        if (hasTextSelectionIn(event.currentTarget)) return;
        // A click on the card body should leave focus where a button click
        // would, so a dialog it opens can hand focus back.
        action?.focus({ preventScroll: true });
        if (linkHref && action) {
          onClick?.(event);
          if (!event.defaultPrevented) {
            followCardLink(
              action as HTMLAnchorElement,
              linkTarget,
              requestsNewTab(event),
            );
          }
          return;
        }
      }
      onClick?.(event);
    };

    const handleLinkAuxClick = (event: React.MouseEvent<HTMLDivElement>) => {
      onAuxClick?.(event);
      const action = actionRef.current;
      if (event.button !== 1 || event.defaultPrevented || !action) return;
      if (!isBodyClick(event)) return;
      event.preventDefault();
      openLinkInNewTab(action as HTMLAnchorElement, linkTarget);
    };

    // A card that isn't a link card doesn't hand its CTAs to an outer one.
    const content = linkHref ? (
      <CardLinkProvider value={{ href: linkHref }}>{children}</CardLinkProvider>
    ) : parentCardLink && !asChild ? (
      <CardLinkProvider value={null}>{children}</CardLinkProvider>
    ) : (
      children
    );

    const renderRoot = (generatedId?: string) => {
      const rootId = props.id ?? generatedId;
      const overlayProps = {
        'aria-label': hasLabel ? ariaLabel : undefined,
        'aria-labelledby': labelFromContent ? rootId : undefined,
        'data-slot': 'card-action',
        className: clipContent
          ? CARD_ACTION_CLIPPED_CLASS_NAME
          : CARD_ACTION_CLASS_NAME,
      };
      return (
        <Component
          ref={ref}
          className={cn(
            cardVariants({
              variant,
              border,
              padding,
              shadow,
              rounded,
              clipContent,
              sectionGap,
              height,
            }),
            isBound && 'relative isolate cursor-pointer',
            cardLinkShellClasses(variant, linkHref != null, hoverShadow),
            isBound && clipContent && CARD_CLIPPED_FOCUS_CLASS_NAME,
            className,
          )}
          style={mergedStyle}
          onClick={isBound ? handleBoundClick : onClick}
          onAuxClick={linkHref ? handleLinkAuxClick : onAuxClick}
          {...(labelFromContent ? { id: rootId } : {})}
          {...props}
          {...(dataHenId ? { 'data-hen-id': dataHenId } : {})}
        >
          {isBound ? (
            <>
              {linkHref ? (
                <>
                  {/* eslint-disable jsx-a11y/anchor-has-content -- the overlay is named by aria-label or aria-labelledby, not by content */}
                  {/* hen-ui-allow-raw-html -- full-card hit area and tab stop; nested controls must stay siblings of it, not descendants */}
                  <a
                    ref={actionRef as React.Ref<HTMLAnchorElement>}
                    href={linkHref}
                    target={linkTarget}
                    rel={
                      linkTarget === '_blank'
                        ? 'noopener noreferrer'
                        : undefined
                    }
                    {...overlayProps}
                  />
                  {/* eslint-enable jsx-a11y/anchor-has-content */}
                </>
              ) : (
                <>
                  {/* hen-ui-allow-raw-html -- full-card hit area and tab stop; nested controls must stay siblings of it, not descendants */}
                  <button
                    ref={actionRef as React.Ref<HTMLButtonElement>}
                    type='button'
                    {...overlayProps}
                  />
                </>
              )}
              {content}
            </>
          ) : (
            content
          )}
        </Component>
      );
    };

    return isBound ? (
      <WithGeneratedId>{renderRoot}</WithGeneratedId>
    ) : (
      renderRoot()
    );
  },
);
CardBase.displayName = 'CardBase';

const Card = React.forwardRef<HTMLDivElement, CardProps>((props, ref) => (
  <CardBase {...props} actionOverlay ref={ref} />
));
Card.displayName = 'Card';

/**
 * Card without the click action overlay, for composites built on Card that
 * forward their own `onClick` to the root and route clicks themselves.
 * Internal to hen-ui: not exported from the package or the catalog, so no
 * author prop can switch the overlay off on the catalog card.
 */
const CardRoot = React.forwardRef<HTMLDivElement, CardProps>((props, ref) => (
  <CardBase {...props} actionOverlay={false} ref={ref} />
));
CardRoot.displayName = 'CardRoot';

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  (
    {
      className,
      height,
      contentPadding,
      stackGap,
      width,
      padding,
      align,
      'align-md': alignMd,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        ref={ref}
        className={cn(
          cardContentVariants({
            height,
            contentPadding,
            stackGap,
            width,
            align,
            'align-md': alignMd,
          }),
          padding,
          className,
        )}
        {...props}
      />
    );
  },
);
CardContent.displayName = 'CardContent';

const footerRoundedMap = { md: 'rounded-b-md', lg: 'rounded-b-lg' } as const;

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  (
    {
      className,
      background = 'bg-secondary-50',
      padding = 'py-d3 px-lg md:px-xl',
      rounded = 'lg',
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        ref={ref}
        className={cn(
          'flex items-center justify-between gap-lg mt-auto w-full [&>*:first-child]:grow',
          footerRoundedMap[rounded],
          background,
          padding,
          className,
        )}
        {...props}
      />
    );
  },
);
CardFooter.displayName = 'CardFooter';

const CardWithSubcomponents = Card as CardComponent;
CardWithSubcomponents.Content = CardContent;
CardWithSubcomponents.Footer = CardFooter;

export { CardWithSubcomponents as Card, CardRoot, CardContent, CardFooter };
